/**
 * DI-3-C — the data sets of a drop, from the PARSE results alone.
 *
 * `groupSet(rows, {withDuplicates: false})` (DI-2-2, called verbatim) gives the
 * position clusters and their provisional flights as soon as the heads are
 * parsed; hashing never gates them. One data set = one cluster with airborne
 * images (a provisional flight). Ground-only clusters and rows without a
 * position are counted on the folder card, never sent — except (DI-3-S74)
 * known-camera rows without a position that joined a flight: they are
 * members, go up without `gps`, and extend the set's window (they never move
 * its centroid); the worker places them from the flight log.
 *
 * ── The set's identity (story §4) ──────────────────────────────────────────
 * `clusterByPosition` numbers its clusters POSITIONALLY: `c${k + 1}` in the
 * order of each cluster's earliest member (time, then row index), counting
 * ground-only clusters too. The number of a set therefore moves whenever the
 * rows change under it — a later-parsed image that is earlier in time opens a
 * cluster before it, "Add another folder" appends rows, and a regrouping with
 * duplicates removes rows. It is not an identity. The set's id here is derived
 * from its CONTENT instead — the earliest member's file name and capture time
 * (`set:<name>@<capturedAtMs>`) — which is the same for the same folder
 * whatever order the browser enumerates the files in and whatever else is in
 * the drop. It keys the row's decisions, the session record and the
 * acquisition's idempotency key (`uuidv5(sessionId + ':' + clusterId)`, the
 * DI-2-3 derivation, unchanged), so a re-drop of the same folder replays.
 *
 * At Start the membership (the row indexes of this tab) is frozen and never
 * re-clustered; at send time only the duplicates INSIDE that membership are
 * dropped (DI-D4 c1, `findDuplicates`, called verbatim).
 */
import { groupSet } from "../group/groupSet.js";
import { findDuplicates } from "../group/duplicates.js";
import type { ClusterGroup, GroupSet, LatLon } from "../group/types.js";
import type { IndexRow } from "../indexStore.js";
import type { CameraFamilyId } from "../parse/types.js";
import type { SyncRow } from "../sync/toDataPointInput.js";
import { exifOffsetMsOf, gateTimeMsOf } from "./gateTime.js";

/**
 * GPS − UTC = 18 s since 2017-01-01 — the gateway's `GPS_UTC_LEAP_S`
 * (`shared/intake-rules/utc-at-exposure.ts`); defined in `gateTime.ts`.
 */
export { GPS_UTC_LEAP_S } from "./gateTime.js";

/** Families that fly without an AFIS unit, so no AFIS log will ever match. */
export const LOG_LESS_FAMILIES: ReadonlySet<CameraFamilyId> = new Set([
  "dji-m4t",
  "dji-h30t",
  "dji-h20t",
]);

export interface TimeWindow {
  startMs: number;
  endMs: number;
  /**
   * DI-3-S70 / DI-TIME-02 — the min / max of the times WITHOUT the EXIF zone
   * offset (the camera clock as written). Present only when the offset moved
   * at least one image (the shape of the gateway's `effectiveImageWindow`
   * `clock`, `effective-capture-time.ts`); the log check tries it when the
   * zoned window passes no log (`api/logCheck.ts:fetchLogCheck`).
   */
  clock?: { startMs: number; endMs: number };
}

export interface ProposalSet {
  /** The stable, content-derived id (module header). */
  clusterId: string;
  /** `clusterByPosition`'s positional id — for display and the files panel only. */
  groupId: string;
  /** 1-based, in the order the sets are listed. */
  ordinal: number;
  centroid: LatLon;
  radiusM: number;
  /** Every row index of the cluster (ground shots and rows without a time included). */
  memberIndexes: number[];
  imageCount: number;
  groundCount: number;
  flightCount: number;
  /** The effective capture window (UTC), null when no member has a time. */
  window: TimeWindow | null;
  families: CameraFamilyId[];
  /** Sum of the members' file sizes, bytes. */
  sizeBytes: number;
}

/**
 * One image's gate time (ms), the way the gateway's DI-D2 time gate reads it
 * (`_BACKEND services/api-gateway/src/shared/intake-rules/gate-time.ts`,
 * DI-D18 c1 amended 2026-10-21, DI-TIME-01; twinned in `gateTime.ts`): a DJI
 * camera's `UTCAtExposure − 18 s`; else the EXIF clock MINUS the camera's
 * stated zone (`OffsetTimeOriginal`, fallback `OffsetTime`); else the EXIF
 * clock as stored. `withZone: false` = the camera clock as written
 * (DI-TIME-02's `clock`). The data points keep the RAW `capturedAtMs`.
 */
export function effectiveCaptureMs(
  row: IndexRow,
  withZone = true,
): number | null {
  const n = row.parsed?.normalized;
  if (!n) return null;
  return gateTimeMsOf(
    n.capturedAtMs ?? null,
    n.utcAtExposureMs ?? null,
    withZone
      ? exifOffsetMsOf({
          offsetTimeOriginal: n.offsetTimeOriginal,
          offsetTime: n.offsetTime,
        })
      : null,
  );
}

/**
 * min / max of the members' gate times; `null` when none has one. DI-TIME-02
 * (the gateway's `effectiveImageWindow`): when the zone offset moved any
 * image, `clock` carries the min / max WITHOUT the offset.
 */
export function effectiveWindow(
  rows: readonly IndexRow[],
  indexes: readonly number[],
): TimeWindow | null {
  let min = Infinity;
  let max = -Infinity;
  let cMin = Infinity;
  let cMax = -Infinity;
  let moved = false;
  for (const i of indexes) {
    const r = rows[i];
    if (!r) continue;
    const t = effectiveCaptureMs(r);
    if (t === null) continue;
    if (t < min) min = t;
    if (t > max) max = t;
    const c = effectiveCaptureMs(r, false);
    if (c === null) continue;
    if (c !== t) moved = true;
    if (c < cMin) cMin = c;
    if (c > cMax) cMax = c;
  }
  if (!Number.isFinite(min)) return null;
  return moved
    ? { startMs: min, endMs: max, clock: { startMs: cMin, endMs: cMax } }
    : { startMs: min, endMs: max };
}

/**
 * DI-3-S70 — the session record's `firstCapturedAtMs` ("the camera clock as
 * written", `session/sessionStore.ts`): the window's start WITHOUT the EXIF
 * zone, i.e. exactly the value it held before the zone was applied to the
 * gate time. It is shown next to the data points' raw times
 * (`flights/AcquisitionFlights.tsx`), so it must not move by the zone.
 */
export function firstCaptureOf(window: TimeWindow | null): number | null {
  return window ? (window.clock?.startMs ?? window.startMs) : null;
}

/** `set:<name>@<capturedAtMs>` of the cluster's earliest member. */
export function stableClusterId(
  cluster: Pick<ClusterGroup, "imageIndexes" | "clusterId">,
  rows: readonly IndexRow[],
): string {
  const first = cluster.imageIndexes[0];
  const row = first === undefined ? undefined : rows[first];
  if (!row) return `set:${cluster.clusterId}`;
  const t = row.parsed?.normalized.capturedAtMs;
  return `set:${row.name}@${t === undefined ? "none" : String(t)}`;
}

/** The data sets of a group set: clusters with airborne images, in order. */
export function proposalSets(
  groups: GroupSet,
  rows: readonly IndexRow[],
): ProposalSet[] {
  const seen = new Map<string, number>();
  return groups.clusters
    .filter((c) => c.flights.length > 0)
    .map((c, k) => {
      let clusterId = stableClusterId(c, rows);
      const dup = seen.get(clusterId) ?? 0;
      seen.set(clusterId, dup + 1);
      if (dup > 0) clusterId = `${clusterId}#${String(dup + 1)}`;
      const families = new Set<CameraFamilyId>();
      let sizeBytes = 0;
      for (const i of c.imageIndexes) {
        const r = rows[i];
        if (!r) continue;
        sizeBytes += r.size;
        if (r.parsed) families.add(r.parsed.family);
      }
      return {
        clusterId,
        groupId: c.clusterId,
        ordinal: k + 1,
        centroid: { lat: c.centroid.lat, lon: c.centroid.lon },
        radiusM: c.radiusM,
        memberIndexes: c.imageIndexes.slice(),
        imageCount: c.imageIndexes.length,
        groundCount: c.ground.length,
        flightCount: c.flights.length,
        window: effectiveWindow(rows, c.imageIndexes),
        families: [...families],
        sizeBytes,
      };
    });
}

/**
 * DI-3-G — the rows of a folder that an "Add images…" binds to ONE existing
 * data set: every member of every set the folder forms (a Start would send
 * exactly these), in row order. Ground-only clusters and rows without a
 * position stay out, as on a drop (DI-3-S74: the known-camera rows without a
 * position that joined a flight are members, as on a drop). `groups` = an
 * already computed `groupForDecide(rows)`.
 */
export function folderMembers(
  rows: readonly IndexRow[],
  groups: GroupSet = groupForDecide(rows),
): number[] {
  return proposalSets(groups, rows)
    .flatMap((s) => s.memberIndexes)
    .sort((a, b) => a - b);
}

/** `groupSet` on the parse results — the sets never wait for a hash. */
export function groupForDecide(rows: readonly IndexRow[]): GroupSet {
  return groupSet(rows, { withDuplicates: false });
}

export interface SendPlan {
  /** True once every member is hashed (or failed to read) — the round trip may start. */
  ready: boolean;
  /** Members to send: parsed, hashed, not an in-set duplicate. */
  send: SyncRow[];
  /** In-set duplicates dropped at send time (DI-D4 c1). */
  duplicates: number;
  /** Members without a hash (a read error) — listed, not sent. */
  unhashed: number;
}

function isSyncRow(r: IndexRow): r is IndexRow & SyncRow {
  return r.parsed !== undefined && r.hashes !== undefined;
}

/**
 * The rows of a frozen membership that go on the wire. `indexDone` = the
 * index phase ended (done / cancelled / error): whatever is hashed by then is
 * all there will be.
 */
export function sendPlan(
  rows: readonly IndexRow[],
  memberIndexes: readonly number[],
  indexDone: boolean,
): SendPlan {
  const members: IndexRow[] = [];
  let pending = 0;
  for (const i of memberIndexes) {
    const r = rows[i];
    if (!r) continue;
    members.push(r);
    if (!r.hashes && !r.error) pending += 1;
  }
  const ready = indexDone || pending === 0;
  const dropped = new Set(
    findDuplicates(members).flatMap((g) => g.droppedIndexes),
  );
  const send: SyncRow[] = [];
  let unhashed = 0;
  for (const r of members) {
    if (dropped.has(r.index)) continue;
    if (isSyncRow(r)) send.push(r);
    else unhashed += 1;
  }
  return { ready, send, duplicates: dropped.size, unhashed };
}
